/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { urlToScreenName } from "../../model/navigation";
import type { UnityRootState } from "../UnityAppStore";
import type { ScreensState } from "./screens.types";
import { NAVIGATION_MAX_STACK_SIZE } from "../../config";

const INITIAL_STATE: ScreensState = {
  activeScreen: "",
  currentUrl: "",
  previousUrl: "",
  screenController: "",
  navigationStack: []
};

/**
 * Redux slice managing the active screen state, URL tracking,
 * and the full navigation history stack.
 *
 * The NavigationEngine reads `activeScreen` via `getActiveScreen()` to
 * determine which ScreenController receives input events.
 */
const screensSlice = createSlice({
  name: "screens",
  initialState: INITIAL_STATE,
  reducers: {
    setActiveScreen(state, action: PayloadAction<string>): void {
      state.activeScreen = action.payload;
    },
    setLocationUrl(state, action: PayloadAction<string>): void {
      if (action.payload === state.currentUrl) {
        return;
      }
      state.previousUrl = state.currentUrl;
      state.currentUrl = action.payload;
    },
    syncScreenFromUrl(state, action: PayloadAction<string>): void {
      state.screenController = urlToScreenName(action.payload);
    },
    /**
     * Push a URL onto the navigation stack.
     * Prunes oldest entries when maxSize is exceeded.
     */
    pushNavigation(state, action: PayloadAction<string>): void {
      state.navigationStack.push(action.payload);
      if (state.navigationStack.length > NAVIGATION_MAX_STACK_SIZE) {
        state.navigationStack = state.navigationStack.slice(
          state.navigationStack.length - NAVIGATION_MAX_STACK_SIZE
        );
      }
    },
    /**
     * Overwrite the top entry with a new URL, mirroring a history REPLACE.
     *
     * A replaced route does not add a history entry, so pushing it here would
     * make `selectPreviousNavUrl()` / `selectCanGoBack()` claim a previous
     * screen that `navigate(-1)` cannot reach — the entry it names was
     * overwritten, not stacked. That mismatch is what left the TV stranded on
     * the player: every route reaching `/player` there replaces.
     *
     * Falls back to a push on an empty stack so a REPLACE arriving before the
     * initial seeding still records where the user is.
     */
    replaceNavigation(state, action: PayloadAction<string>): void {
      if (state.navigationStack.length === 0) {
        state.navigationStack.push(action.payload);
        return;
      }
      state.navigationStack[state.navigationStack.length - 1] = action.payload;
    },
    /**
     * Pop the current entry off the stack (for back navigation).
     */
    popNavigation(state): void {
      if (state.navigationStack.length > 1) {
        state.navigationStack.pop();
      }
    },
    /**
     * Clear the entire navigation stack (e.g. on sign-out or reset).
     */
    resetNavigationStack(state): void {
      state.navigationStack = [];
    }
  }
});

export const {
  setActiveScreen,
  setLocationUrl,
  syncScreenFromUrl,
  pushNavigation,
  replaceNavigation,
  popNavigation,
  resetNavigationStack
} = screensSlice.actions;

// ─── Selectors ───

export const selectActiveScreen = (state: UnityRootState): string =>
  state.screens.activeScreen;

export const selectCurrentUrl = (state: UnityRootState): string =>
  state.screens.currentUrl;

export const selectPreviousUrl = (state: UnityRootState): string =>
  state.screens.previousUrl;

export const selectScreenController = (state: UnityRootState): string =>
  state.screens.screenController;

/** Full navigation history stack (list of URL paths). */
export const selectNavigationStack = (state: UnityRootState): string[] =>
  state.screens.navigationStack;

/** Previous URL in the stack (one below top), or null if unavailable. */
export const selectPreviousNavUrl = (state: UnityRootState): string | null => {
  const stack = state.screens.navigationStack;
  return stack.length > 1 ? stack[stack.length - 2] : null;
};

/** Number of entries in the stack. */
export const selectNavigationStackDepth = (state: UnityRootState): number =>
  state.screens.navigationStack.length;

/** Whether back navigation is possible (more than one entry). */
export const selectCanGoBack = (state: UnityRootState): boolean =>
  state.screens.navigationStack.length > 1;

export default screensSlice.reducer;
